import React, { useCallback, useEffect, useState } from 'react';
import { Button, InputNumber, Modal, notification, TableProps } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { IDataModal } from '@/interfaces/dataModal';
import { IOrder, IOrderItem } from '@/interfaces/order';
import { returnPartialOrder } from '@/adapters/order';
import TableComponent from '@/components/table';
import { formatNumber } from '@/utils/dataUtils';
import { debounce, round, sumBy } from 'lodash';

interface IProps {
  dataModal?: IDataModal<IOrder>;
  handleOk: () => void;
  handleCancel: () => void;
}

const OrderPartialReturnOrder = ({
  dataModal,
  handleOk,
  handleCancel,
}: IProps) => {
  const [orders, setOrders] = useState<Partial<IOrderItem>[]>([]);

  const mutations = useMutation({
    mutationFn: async (data: Partial<IOrder>) => returnPartialOrder(data),
    mutationKey: ['update-order-return'],
    onSuccess: data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        notification.success({
          message: 'คืนสินค้าสำเร็จ',
        });
        handleOk();
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async () => {
    const order = dataModal?.item;
    if (order) {
      const isFullReturn = orders.every(
        item => item.returnQuantity === item.quantity,
      );
      order.isPatialReturn = !isFullReturn;
      order.orderItems = [...orders]?.map(c => {
        c.quantity = Number(c.returnQuantity || 0);
        return c;
      });
      await mutations.mutateAsync(order);
    }
  };

  useEffect(() => {
    if (dataModal?.item?.orderItems?.length) {
      setOrders(dataModal?.item?.orderItems);
    }
  }, [dataModal?.item?.orderItems?.length]);

  const debouncedSearch = useCallback(
    debounce((rec, value: number) => {
      setOrders(prev =>
        prev.map(item =>
          item.id == rec.id
            ? {
                ...item,
                returnQuantity: value,
              }
            : item,
        ),
      );
    }, 300),
    [],
  );

  const columns: TableProps<Partial<IOrderItem>>['columns'] = [
    {
      title: 'รหัสสินค้า',
      dataIndex: 'productCode',
      width: 130,
      className: 'font-bold',
      align: 'center',
    },
    {
      title: 'สินค้า',
      dataIndex: 'productName',
      className: 'font-bold',
    },
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      width: 120,
      className: 'font-bold',
      align: 'center',
    },
    {
      title: 'จำนวน Package',
      width: 120,
      dataIndex: 'package',
      align: 'right',
      render: (_: number, rec) =>
        formatNumber(
          round(Number(rec?.quantity) / Number(rec?.conversion), 2),
          2,
        ),
    },
    {
      title: 'Package',
      width: 80,
      dataIndex: 'packageUnitName',
      align: 'center',
    },
    {
      title: 'จำนวน',
      width: 100,
      dataIndex: 'quantity',
      align: 'right',
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: 'หน่วย',
      width: 80,
      dataIndex: 'baseUnitName',
      className: 'font-bold',
      align: 'center',
    },
    {
      title: 'Package',
      width: 60,
      dataIndex: 'returnPackage',
      align: 'right',
      className: 'text-red-500 font-bold',
      render: (_: number, rec) =>
        formatNumber(
          round(Number(rec?.returnQuantity || 0) / Number(rec?.conversion), 2),
          2,
        ),
    },
    {
      title: 'Return Qty',
      dataIndex: 'returnQuantity',
      key: 'returnQuantity',
      className: 'font-bold text-blue-600',
      align: 'right',
      width: 120,
      render: (_, rec) => (
        <InputNumber
          type="number"
          placeholder="Return Qty"
          className="w-full"
          value={rec.returnQuantity}
          min={0}
          max={rec.quantity}
          onChange={v => debouncedSearch(rec, Number(v))}
        />
      ),
    },
  ];

  const disabled = sumBy(orders, c => Number(c.returnQuantity || 0)) > 0;

  return (
    <Modal
      title={`คืนสินค้า`}
      open={dataModal?.open}
      onOk={() => handleUpdate()}
      onCancel={handleCancel}
      okText="บันทึก"
      cancelText="ยกเลิก"
      width={1200}
      footer={[
        <Button key="back" onClick={handleCancel}>
          ยกเลิก
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={mutations.isPending}
          disabled={!disabled}
          onClick={handleUpdate}
        >
          บันทึก
        </Button>,
      ]}
      afterClose={() => {
        setOrders([]);
      }}
    >
      <div className="flex w-full">
        <TableComponent<Partial<IOrderItem>>
          columns={columns}
          criteria={{}}
          data={{
            data: orders,
            totalRecords: 0,
          }}
          loading={false}
          onTableChange={() => {
            console.log();
          }}
          scroll={false}
          showPagination={false}
        />
      </div>
    </Modal>
  );
};

export default OrderPartialReturnOrder;
